/**
 * ActiveSyncStatus Component
 * 
 * Unified sync status display component for both admin and inspector interfaces.
 * Replaces duplicate implementations in ActiveSyncs page and SyncStatusBar.
 */

import React, { useState, useCallback, useMemo } from 'react'
import {
  Box,
  LinearProgress,
  Typography,
  Stack,
  Chip,
  Collapse,
  IconButton,
  Alert,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Container
} from '@mui/material'
import {
  CloudSync as SyncIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material'
import { SyncOperation } from '../../services/api'
import { poolwerxColors } from '../../theme/colors'
import { getSystemIcon, LightspeedIcon, XeroIcon, SkimmerIcon } from '../SystemIcons'
import { SYNC_STATUS_LABELS, SYNC_STATUS_QUEUED_LABEL, formatSystemName } from './syncLabels'

interface ActiveSyncStatusProps {
  operations: SyncOperation[]
  isLoading?: boolean
  fpCode?: string // For inspector-specific filtering
  compact?: boolean // For header/inline display modes
  showBulkActions?: boolean // For admin bulk operations
  onSyncComplete?: () => void // Completion callback
  onRefresh?: () => void
  onDismiss?: () => void
  visible?: boolean // Control visibility (for inspector auto-hide)
  showConnectionStatus?: boolean
  connectionStatus?: {
    isConnected: boolean
    status: string
  }
}

const ActiveSyncStatus: React.FC<ActiveSyncStatusProps> = ({
  operations = [],
  isLoading = false,
  fpCode,
  compact = false,
  showBulkActions = false,
  onSyncComplete,
  onRefresh,
  onDismiss,
  visible = true,
  showConnectionStatus = false,
  connectionStatus
}) => {
  const [dismissed, setDismissed] = useState(false)
  const [expanded, setExpanded] = useState(false)

  // Filter operations by FP code if provided (inspector mode)
  const filteredOperations = useMemo(() => {
    if (!fpCode) return operations
    return operations.filter(op => op.fp_code === fpCode.toLowerCase())
  }, [operations, fpCode])

  // Statistics calculations
  const stats = useMemo(() => {
    const safeOperations = filteredOperations || []

    const pendingCount = safeOperations.filter(op => op.status === 'pending').length
    const runningCount = safeOperations.filter(op => op.status === 'running').length
    const avgProgress = safeOperations.length > 0
      ? safeOperations.reduce((sum, op) => sum + (op.progress_percentage || 0), 0) / safeOperations.length
      : 0

    // Per-system breakdown
    const totalBySystem = {
      lightspeed: safeOperations.filter(op => op.sync_system === 'lightspeed').length,
      xero: safeOperations.filter(op => op.sync_system === 'xero').length,
      skimmer: safeOperations.filter(op => op.sync_system === 'skimmer').length,
    }
    const runningBySystem = {
      lightspeed: safeOperations.filter(op => op.status === 'running' && op.sync_system === 'lightspeed').length,
      xero: safeOperations.filter(op => op.status === 'running' && op.sync_system === 'xero').length,
      skimmer: safeOperations.filter(op => op.status === 'running' && op.sync_system === 'skimmer').length,
    }
    const pendingBySystem = {
      lightspeed: safeOperations.filter(op => op.status === 'pending' && op.sync_system === 'lightspeed').length,
      xero: safeOperations.filter(op => op.status === 'pending' && op.sync_system === 'xero').length,
      skimmer: safeOperations.filter(op => op.status === 'pending' && op.sync_system === 'skimmer').length,
    }

    return {
      total: safeOperations.length,
      pending: pendingCount,
      running: runningCount,
      totalBySystem,
      runningBySystem,
      pendingBySystem,
      avgProgress: Math.round(avgProgress)
    }
  }, [filteredOperations])

  // Get system icon using the centralized SystemIcons
  const getSystemIconComponent = useCallback((system: string, size = 18) => {
    const icon = getSystemIcon(system, { size })
    return icon || <SyncIcon sx={{ fontSize: size }} />
  }, [])

  // Get system color
  const getSystemColor = useCallback((system: string) => {
    switch (system.toLowerCase()) {
      case 'lightspeed':
        return '#2196f3' // Blue
      case 'xero':
        return '#13b5ea' // Xero blue
      case 'skimmer':
        return '#00bcd4' // Cyan
      default:
        return '#666'
    }
  }, [])

  // System badge component for displaying counts
  const SystemBadge: React.FC<{ system: 'lightspeed' | 'xero' | 'skimmer'; count: number }> = ({ system, count }) => {
    if (count === 0) return null
    const Icon = system === 'lightspeed' ? LightspeedIcon : system === 'xero' ? XeroIcon : SkimmerIcon
    const bgColor = system === 'lightspeed' ? 'rgba(33, 150, 243, 0.1)' : system === 'xero' ? 'rgba(19, 181, 234, 0.1)' : 'rgba(0, 188, 212, 0.1)'
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 2,
        bgcolor: bgColor,
      }}>
        <Icon size={18} />
        <Typography variant="body2" fontWeight={600}>{count}</Typography>
      </Box>
    )
  }

  // Format progress text
  const formatProgress = useCallback((sync: SyncOperation) => {
    if (sync.records_total && sync.records_processed !== undefined) {
      return `${sync.records_processed.toLocaleString()} / ${sync.records_total.toLocaleString()} records`
    }
    return `${sync.progress_percentage}% complete`
  }, [])

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setDismissed(true)
    if (onDismiss) {
      onDismiss()
    }
  }, [onDismiss])

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh()
    }
  }, [onRefresh])

  // Don't render if not visible or dismissed
  if (!visible || dismissed) {
    return null
  }

  // Compact mode (for inspector header)
  if (compact) {
    // No operations - show completion message if we just finished
    if (filteredOperations.length === 0) {
      return (
        <Collapse in={visible} timeout={300}>
          <Box
            sx={{
              position: 'sticky',
              top: { xs: '64px', md: '120px' },
              zIndex: 30,
              backgroundColor: '#EAF6F8',
              borderBottom: '1px solid #B7DDE4',
            }}
          >
            <Container maxWidth="xl" sx={{ px: { xs: 3, sm: 3 } }}>
              <Box sx={{ py: 0.5 }}>
                <Alert
                  severity="success"
                  icon={<CheckIcon />}
                  sx={{
                    py: 0.25,
                    backgroundColor: 'transparent',
                    color: poolwerxColors.charcoal,
                    '& .MuiAlert-message': { fontSize: '0.85rem' },
                  }}
                  action={
                    <IconButton
                      size="small"
                      onClick={handleDismiss}
                      aria-label="Dismiss"
                      sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  Data synced - latest snapshot is ready
                </Alert>
              </Box>
            </Container>
          </Box>
        </Collapse>
      )
    }

    const renderSummaryChip = (sync: SyncOperation) => {
      const label = formatSystemName(sync.sync_system)
      const detail = sync.status === 'running'
        ? `${sync.progress_percentage ?? 0}%`
        : sync.status === 'pending'
          ? SYNC_STATUS_QUEUED_LABEL
          : SYNC_STATUS_LABELS[sync.status] ?? sync.status
      return (
        <Chip
          key={sync.operation_id}
          size="small"
          icon={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getSystemIconComponent(sync.sync_system, 16)}
            </Box>
          }
          label={`${label} · ${detail}`}
          sx={{
            height: 28,
            bgcolor: '#FFFFFF',
            border: '1px solid #CFE4EA',
            color: poolwerxColors.charcoal,
            '& .MuiChip-icon': { ml: 1.25, mr: 0 },
            '& .MuiChip-label': {
              fontSize: '0.8rem',
              fontWeight: 500,
              pl: 1.25,
              pr: 1.5,
            },
          }}
        />
      )
    }

    return (
      <Collapse in={visible} timeout={300}>
        <Box
          sx={{
            position: 'sticky',
            top: { xs: '64px', md: '120px' },
            zIndex: 30,
            backgroundColor: '#EAF6F8',
            borderBottom: '1px solid #B7DDE4',
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 3, sm: 3 } }}>
            <Box sx={{ pt: '7px', pb: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CircularProgress size={14} sx={{ color: poolwerxColors.teal }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: poolwerxColors.charcoal,
                    flexShrink: 0,
                  }}
                >
                  Syncing latest data
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flex: 1, flexWrap: 'wrap', rowGap: 0.5 }}
                >
                  {filteredOperations.map(renderSummaryChip)}
                </Stack>
                <Button
                  size="small"
                  onClick={() => setExpanded((x) => !x)}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  aria-expanded={expanded}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
                >
                  Details
                </Button>
                <IconButton
                  size="small"
                  onClick={handleDismiss}
                  aria-label="Dismiss"
                  sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Container>

          <Collapse in={expanded} timeout={200} unmountOnExit>
            <Box
              sx={{
                backgroundColor: '#F6FBFC',
                borderTop: '1px solid #B7DDE4',
              }}
            >
              <Container maxWidth="xl" sx={{ px: { xs: 3, sm: 3 } }}>
                <Stack
                  sx={{
                    // Indent ~26px so each row tucks under "Syncing latest data"
                    // (spinner 14px + spacing 12px in the collapsed header).
                    pl: '26px',
                  }}
                >
                  {filteredOperations.map((sync, idx) => {
                    const isRunning = sync.status === 'running'
                    const statusLabel = sync.status === 'pending'
                      ? SYNC_STATUS_QUEUED_LABEL
                      : SYNC_STATUS_LABELS[sync.status] ?? sync.status
                    return (
                      <Box
                        key={sync.operation_id}
                        sx={{
                          py: 0.375,
                          borderTop: idx > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ minHeight: 24 }}
                        >
                          {/* Col 1: system / status */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ width: 180, flexShrink: 0 }}
                          >
                            {getSystemIconComponent(sync.sync_system, 16)}
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, fontSize: '0.8rem', color: poolwerxColors.charcoal }}
                            >
                              {formatSystemName(sync.sync_system)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                opacity: 0.85,
                              }}
                            >
                              {statusLabel}
                            </Typography>
                          </Stack>

                          {/* Col 2: current step (takes remaining width) */}
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              opacity: 0.75,
                            }}
                          >
                            {sync.current_step}
                          </Typography>

                          {/* Col 3: fixed-width progress area so bars + percentages
                              line up cleanly across rows */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ flexShrink: 0, width: 226, justifyContent: 'flex-end' }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={sync.progress_percentage || 0}
                              sx={{
                                width: 180,
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0,0,0,0.06)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: isRunning
                                    ? '#5BB7C4'
                                    : 'rgba(0,0,0,0.2)',
                                  borderRadius: 2,
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontVariantNumeric: 'tabular-nums',
                                minWidth: 32,
                                textAlign: 'right',
                              }}
                            >
                              {sync.progress_percentage ?? 0}%
                            </Typography>
                          </Stack>
                        </Stack>

                        {sync.error_message && (
                          <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 0.5, py: 0 }}>
                            {sync.error_message}
                          </Alert>
                        )}
                      </Box>
                    )
                  })}
                </Stack>
              </Container>
            </Box>
          </Collapse>

          {stats.running > 0 && (
            <LinearProgress
              variant="determinate"
              value={stats.avgProgress}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: 'transparent',
                '& .MuiLinearProgress-bar': { backgroundColor: '#5BB7C4' },
              }}
            />
          )}
        </Box>
      </Collapse>
    )
  }

  // Full mode (for admin dashboard)
  return (
    <Box>
      {/* Overall Progress Bar */}
      {stats.running > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Overall Progress
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stats.avgProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: poolwerxColors.teal
                }
              }}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Average: {stats.avgProgress}% across {stats.running} running sync{stats.running !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      {stats.total > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                  Total
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h4" component="div" fontWeight={600}>
                    {stats.total}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <SystemBadge system="lightspeed" count={stats.totalBySystem.lightspeed} />
                    <SystemBadge system="xero" count={stats.totalBySystem.xero} />
                    <SystemBadge system="skimmer" count={stats.totalBySystem.skimmer} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                  Running
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h4" component="div" fontWeight={600} sx={{ color: poolwerxColors.teal }}>
                    {stats.running}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <SystemBadge system="lightspeed" count={stats.runningBySystem.lightspeed} />
                    <SystemBadge system="xero" count={stats.runningBySystem.xero} />
                    <SystemBadge system="skimmer" count={stats.runningBySystem.skimmer} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                  Pending
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h4" component="div" fontWeight={600} color="primary">
                    {stats.pending}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <SystemBadge system="lightspeed" count={stats.pendingBySystem.lightspeed} />
                    <SystemBadge system="xero" count={stats.pendingBySystem.xero} />
                    <SystemBadge system="skimmer" count={stats.pendingBySystem.skimmer} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Loading state - removed to prevent constant "Loading active syncs..." message */}

      {/* Empty state - removed per request */}

    </Box>
  )
}

export default ActiveSyncStatus